// TopNav — nav superior sticky de la tienda v5 (logo + links desktop + acciones + drawer mobile).
// Fuente: maquetas. En ≥900px muestra los links inline; en <900px el ☰ abre un drawer
// con el set completo de links (incluye Programa Cuadra, junto a Barrios).
//
// Self-contained: <style>{NAV_CSS}</style> + clases .tv5-nav-* + tokens --tv5-* propios.
// Touch: .tv5-nav-icon ≥44×44. Drawer con estado local (useState), no observers.
//
// Props:
//   active     'home'|'kits'|'tienda'|'como'|'barrios'|'cuadra'  → link activo
//   cartCount  número   → OVERRIDE del badge del carrito (default: useCart().itemsCount; solo lo pasa el QA)
//   accent     'teal'|'gold'  → color de acento (Tienda usa gold; resto teal). default 'teal'
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { TV5_ROUTES, TV5_EXTERNAL } from './routes';
import { useCart } from '../../lib/CartContext';
import { useAuth } from '../../lib/AuthContext';

const NAV = [
  { key: 'home', label: 'Home', to: TV5_ROUTES.home },
  { key: 'kits', label: 'Kits', to: TV5_ROUTES.kits },
  { key: 'tienda', label: 'Tienda', to: TV5_ROUTES.tienda },
  { key: 'limpackflex', label: 'LimpackFlex', to: TV5_ROUTES.limpackflex },
  { key: 'empresas', label: 'Empresas', to: TV5_ROUTES.empresas },
  { key: 'como', label: 'Cómo funciona', to: TV5_ROUTES.comoFunciona },
  { key: 'barrios', label: 'Barrios', to: TV5_ROUTES.barrios },
  { key: 'cuadra', label: 'Programa Cuadra', to: TV5_ROUTES.cuadra },
];

const NAV_CSS = `
.tv5-nav {
  --tv5-teal:#00A8B5; --tv5-gold:#C5A059; --tv5-navy:#1A3461; --tv5-accent:var(--tv5-teal);
  --tv5-text:#F8F6F1; --tv5-muted:rgba(248,246,241,0.65); --tv5-border:rgba(255,255,255,0.08);
  position:sticky; top:0; z-index:100; background:rgba(10,20,36,0.9);
  backdrop-filter:blur(20px); -webkit-backdrop-filter:blur(20px);
  border-bottom:1px solid var(--tv5-border); padding:12px 16px;
  display:flex; align-items:center; justify-content:space-between;
  font-family:'Inter',-apple-system,BlinkMacSystemFont,sans-serif;
}
.tv5-nav--gold { --tv5-accent:var(--tv5-gold); }
.tv5-nav-logo-img { display:block; height:28px; width:auto; }
@media (min-width:900px){ .tv5-nav-logo-img { height:32px; } }
.tv5-nav-links { display:none; gap:22px; align-items:center; }
.tv5-nav-links a { color:var(--tv5-muted); text-decoration:none; font-size:13px; font-weight:500; }
.tv5-nav-links a.active { color:var(--tv5-accent); }
@media (hover:hover){ .tv5-nav-links a:hover { color:var(--tv5-accent); } }
@media (min-width:900px){ .tv5-nav-links { display:flex; } .tv5-nav-burger { display:none !important; } }
.tv5-nav-actions { display:flex; gap:8px; align-items:center; }
.tv5-nav-icon {
  background:rgba(255,255,255,0.06); border:1px solid var(--tv5-border); color:var(--tv5-text);
  padding:10px 12px; min-width:44px; min-height:44px; display:inline-flex; align-items:center;
  justify-content:center; border-radius:10px; font-size:15px; cursor:pointer; position:relative;
  text-decoration:none;
}
.tv5-nav-cart-badge {
  position:absolute; top:-4px; right:-4px; background:var(--tv5-teal); color:var(--tv5-navy);
  font-size:10px; font-weight:900; padding:2px 6px; border-radius:99px; min-width:18px;
  text-align:center; box-shadow:0 4px 10px rgba(0,168,181,0.4); line-height:1;
}
/* Cuenta: solo desktop (en mobile está en el BottomNav). Con label para que se encuentre. */
.tv5-nav-cuenta { display:none; }
.tv5-nav-cuenta-txt { display:none; }
@media (min-width:900px){
  .tv5-nav-cuenta { display:inline-flex; gap:7px; padding:10px 14px; font-size:13px; font-weight:600; }
  .tv5-nav-cuenta-txt { display:inline; }
}
@media (hover:hover){
  .tv5-nav-cuenta:hover { background:rgba(255,255,255,0.10); border-color:var(--tv5-accent); color:var(--tv5-accent); }
}
/* Drawer mobile */
.tv5-nav-drawer-bg { position:fixed; inset:0; background:rgba(0,0,0,0.6); backdrop-filter:blur(4px);
  z-index:110; opacity:0; pointer-events:none; transition:opacity 0.25s; }
.tv5-nav-drawer-bg.open { opacity:1; pointer-events:auto; }
.tv5-nav-drawer {
  position:fixed; top:0; right:0; bottom:0; width:min(300px,80vw); z-index:111;
  background:#0A1424; border-left:1px solid var(--tv5-border); padding:20px;
  transform:translateX(100%); transition:transform 0.3s cubic-bezier(0.34,1.56,0.64,1);
  display:flex; flex-direction:column; gap:4px;
}
.tv5-nav-drawer.open { transform:translateX(0); }
.tv5-nav-drawer a { color:var(--tv5-text); text-decoration:none; font-size:15px; font-weight:600;
  padding:14px 12px; border-radius:10px; min-height:44px; display:flex; align-items:center; }
.tv5-nav-drawer a.active { color:var(--tv5-accent); background:rgba(255,255,255,0.04); }
.tv5-nav-drawer-close { align-self:flex-end; background:rgba(255,255,255,0.06); border:1px solid var(--tv5-border);
  color:var(--tv5-muted); width:44px; height:44px; border-radius:10px; font-size:20px; cursor:pointer; margin-bottom:8px; }
@media (min-width:900px){ .tv5-nav-drawer, .tv5-nav-drawer-bg { display:none; } }
@media (prefers-reduced-motion:reduce){ .tv5-nav-drawer, .tv5-nav-drawer-bg { transition:none; } }
`;

export function TopNav({ active, cartCount, accent = 'teal' }) {
  const [open, setOpen] = useState(false);
  const { itemsCount } = useCart();
  const { user } = useAuth();
  const badge = cartCount ?? itemsCount; // prop pisa (QA); default = carrito real
  // Sin sesión el botón invita a entrar/registrarse; con sesión va derecho a la cuenta.
  const cuentaLabel = user ? 'Cuenta' : 'Ingresar';
  const cuentaTitle = user ? 'Mi cuenta' : 'Ingresá o creá tu cuenta';
  return (
    <>
      <nav className={'tv5-nav' + (accent === 'gold' ? ' tv5-nav--gold' : '')}>
        <style>{NAV_CSS}</style>
        <Link to={TV5_ROUTES.home} className="tv5-nav-logo" aria-label="Limpack — Home">
          <img src="/logo-nav.png" alt="Limpack" className="tv5-nav-logo-img" />
        </Link>
        <div className="tv5-nav-links">
          {NAV.map((l) => (
            <Link key={l.key} to={l.to} className={active === l.key ? 'active' : undefined}>{l.label}</Link>
          ))}
        </div>
        <div className="tv5-nav-actions">
          <Link to={`${TV5_ROUTES.tienda}?buscar=1`} className="tv5-nav-icon" title="Buscar" aria-label="Buscar">🔍</Link>
          <Link to={TV5_ROUTES.checkout} className="tv5-nav-icon" title="Carrito" aria-label="Carrito">
            🛒
            {badge > 0 && <span className="tv5-nav-cart-badge">{badge}</span>}
          </Link>
          {/* Cuenta: SOLO desktop (≥900px). En mobile ya está en el BottomNav → no duplicar.
              Link externo: el panel del cliente vive en otro subdominio (panel.limpack.uy). */}
          <a
            href={TV5_EXTERNAL.cuenta}
            className="tv5-nav-icon tv5-nav-cuenta"
            title={cuentaTitle}
            aria-label={cuentaTitle}
          >
            <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
              <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
            </svg>
            <span className="tv5-nav-cuenta-txt">{cuentaLabel}</span>
          </a>
          <button className="tv5-nav-icon tv5-nav-burger" aria-label="Menú" onClick={() => setOpen(true)}>☰</button>
        </div>
      </nav>

      {/* Drawer FUERA del <nav>: el backdrop-filter del nav crea un containing block que atrapaba
          los position:fixed del drawer/backdrop → no cubrían el viewport. Fix Nico. */}
      <div className={'tv5-nav-drawer-bg' + (open ? ' open' : '')} onClick={() => setOpen(false)} />
      <div className={'tv5-nav-drawer' + (open ? ' open' : '')} role="dialog" aria-label="Menú">
        <button className="tv5-nav-drawer-close" onClick={() => setOpen(false)} aria-label="Cerrar menú">×</button>
        {NAV.map((l) => (
          <Link key={l.key} to={l.to} className={active === l.key ? 'active' : undefined} onClick={() => setOpen(false)}>{l.label}</Link>
        ))}
      </div>
    </>
  );
}
